import React from "react";
import { motion } from "framer-motion";

/**
 * Reveal a long body of text paragraph-by-paragraph as the user scrolls.
 * Each paragraph is split into "lines" using whitespace-aware chunking so the
 * reveal feels editorial, like type setting itself line by line on a page.
 *
 * Props:
 *   text: string (newline-separated paragraphs)
 *   className: wrapper classes
 *   tone: "tobacco" | "cream"
 */
export default function ScrollReveal({ text, className = "", tone = "tobacco" }) {
  if (!text) return null;
  const paragraphs = String(text).split(/\n+/).map((p) => p.trim()).filter(Boolean);
  const colorClass = tone === "tobacco" ? "text-tobacco/85" : "text-cream/85";

  return (
    <div className={`space-y-9 ${className}`}>
      {paragraphs.map((para, p) => (
        <Paragraph key={p} text={para} colorClass={colorClass} index={p} />
      ))}
    </div>
  );
}

function Paragraph({ text, colorClass, index }) {
  // Split into pseudo-lines of ~80 chars (so each line reveals on scroll).
  const lines = chunkByLength(text, 90);
  return (
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
      className={`font-light leading-[1.85] text-[17px] md:text-[18px] ${colorClass}`}
    >
      {lines.map((ln, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{
            duration: 1.6,
            delay: 0.15 + i * 0.18,
            ease: [0.65, 0, 0.35, 1],
          }}
          className="block"
          style={{ textWrap: "pretty" }}
        >
          {ln}
        </motion.span>
      ))}
    </motion.p>
  );
}

function chunkByLength(text, maxChars) {
  const words = text.split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars && cur) {
      lines.push(cur.trim());
      cur = w;
    } else {
      cur = cur ? cur + " " + w : w;
    }
  }
  if (cur) lines.push(cur.trim());
  return lines;
}
